import {
  createBoundsFromLatLng,
  formatLocalDateYMD,
  serializeBounds,
} from './homepageSearchOptions';

const trimString = value => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const resolveDateInput = date => {
  if (!date) {
    return undefined;
  }
  if (date.date) {
    return formatLocalDateYMD(date.date);
  }
  return formatLocalDateYMD(date);
};

/**
 * Parse YYYY-MM-DD into a local calendar Date (no UTC day-shift).
 * @param {string} ymd
 * @returns {Date|undefined}
 */
export const parseLocalDateYMD = ymd => {
  if (typeof ymd !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return undefined;
  }
  const [year, month, day] = ymd.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

/**
 * Resolve start/end YYYY-MM-DD strings from date range form value or legacy single date.
 *
 * @param {Object} input
 * @param {{ startDate?: Date, endDate?: Date }} [input.dateRange] - FieldDateRangePicker value
 * @param {string|Date|{ date: Date }} [input.date] - legacy FieldSingleDatePicker value
 * @param {string|Date} [input.startDate]
 * @param {string|Date} [input.endDate]
 * @returns {{ start: string, end: string }|undefined}
 */
export const resolveDateRangeYMD = ({ dateRange, date, startDate, endDate } = {}) => {
  if (dateRange?.startDate) {
    const start = formatLocalDateYMD(dateRange.startDate);
    const end = formatLocalDateYMD(dateRange.endDate || dateRange.startDate);
    return start && end ? { start, end } : undefined;
  }

  const explicitStart = formatLocalDateYMD(startDate);
  const explicitEnd = formatLocalDateYMD(endDate || startDate);
  if (explicitStart && explicitEnd) {
    return { start: explicitStart, end: explicitEnd };
  }

  const single = resolveDateInput(date);
  if (single) {
    return { start: single, end: single };
  }

  return undefined;
};

/**
 * Build initial dateRange form value from URL/search query params.
 * Supports dates (start,end), startDate+endDate, and legacy date.
 *
 * @param {Object} query
 * @param {string} [query.dates] - "YYYY-MM-DD,YYYY-MM-DD"
 * @param {string} [query.startDate]
 * @param {string} [query.endDate]
 * @param {string} [query.date] - legacy single-day param
 * @returns {{ startDate: Date, endDate: Date }|null}
 */
export const parseDateRangeForSearchForm = ({ dates, startDate, endDate, date } = {}) => {
  let startYMD;
  let endYMD;

  if (typeof dates === 'string' && dates.includes(',')) {
    const parts = dates.split(',');
    startYMD = parts[0]?.trim();
    endYMD = parts[1]?.trim() || startYMD;
  } else if (startDate || endDate) {
    startYMD = typeof startDate === 'string' ? startDate.trim() : undefined;
    endYMD = typeof endDate === 'string' ? endDate.trim() : startYMD;
  } else if (typeof date === 'string' && date.trim()) {
    startYMD = date.trim();
    endYMD = startYMD;
  }

  const start = parseLocalDateYMD(startYMD);
  const end = parseLocalDateYMD(endYMD || startYMD);

  if (start && end) {
    return { startDate: start, endDate: end };
  }

  return null;
};

/**
 * Merge topbar/search form params into existing SearchPage query params.
 * Keeps `dates` in sync and drops legacy startDate/endDate/date keys when using dates.
 */
export const mergeTopbarSearchIntoQuery = (currentParams = {}, submittedParams = {}) => {
  const merged = { ...currentParams, ...submittedParams };

  if (Object.prototype.hasOwnProperty.call(submittedParams, 'dates')) {
    delete merged.startDate;
    delete merged.endDate;
    delete merged.date;
    if (!submittedParams.dates) {
      delete merged.dates;
    }
  }

  return merged;
};

/**
 * Update SearchPage query params from a topbar date range picker change.
 */
export const applySearchPageDateQueryParams = (currentParams = {}, dateRange) => {
  const dateParams = buildListingSearchParams({ target: 'searchPage', dateRange });
  if (!dateParams.dates) {
    return mergeTopbarSearchIntoQuery(currentParams, { dates: null });
  }
  return mergeTopbarSearchIntoQuery(currentParams, dateParams);
};

/**
 * Build search parameters for landing-page in-page results or Sharetribe SearchPage navigation.
 *
 * @param {Object} input
 * @param {'landing'|'searchPage'} input.target
 * @param {string} [input.location] - address string
 * @param {{ lat?: number, lng?: number }} [input.origin]
 * @param {object|string} [input.bounds]
 * @param {string|Date} [input.date] - legacy single date
 * @param {{ startDate?: Date, endDate?: Date }} [input.dateRange]
 * @param {string|Date} [input.startDate]
 * @param {string|Date} [input.endDate]
 * @param {string} [input.category] - category id
 * @param {string} [input.subcategory] - subcategory id
 * @param {string} [input.keyword]
 * @param {string} [input.sessionType] - In-Person | Online | Camp
 */
export const buildListingSearchParams = ({
  target = 'landing',
  location,
  origin,
  bounds,
  date,
  dateRange,
  startDate,
  endDate,
  category,
  subcategory,
  keyword,
  sessionType,
} = {}) => {
  const address = trimString(location);
  const categoryValue = trimString(category);
  const subCategoryValue = trimString(subcategory);
  const keywordValue = trimString(keyword);
  const sessionTypeValue = trimString(sessionType);
  const dateRangeYMD = resolveDateRangeYMD({ dateRange, date, startDate, endDate });

  let boundsValue = bounds;
  if (!boundsValue && origin?.lat != null && origin?.lng != null) {
    boundsValue = createBoundsFromLatLng(origin.lat, origin.lng);
  }
  const boundsString = serializeBounds(boundsValue);

  if (target === 'searchPage') {
    const params = {};

    if (keywordValue) {
      params.keywords = keywordValue;
    }
    if (categoryValue) {
      params.pub_categoryLevel1 = categoryValue;
    }
    if (subCategoryValue) {
      params.pub_categoryLevel2 = subCategoryValue;
    }
    if (address) {
      params.address = address;
    }
    if (boundsString) {
      params.bounds = boundsValue;
    }
    if (origin?.lat != null && origin?.lng != null) {
      params.origin = origin;
    }
    if (dateRangeYMD) {
      params.dates = `${dateRangeYMD.start},${dateRangeYMD.end}`;
    }

    return params;
  }

  // Landing page provider/session APIs
  const landingParams = {};

  if (categoryValue) {
    landingParams.category = categoryValue;
  }
  if (subCategoryValue) {
    landingParams.subCategory = subCategoryValue;
  }
  if (keywordValue) {
    landingParams.keyword = keywordValue;
  }
  if (sessionTypeValue) {
    landingParams.sessionType = sessionTypeValue;
  }
  if (address) {
    landingParams.address = address;
  }
  if (boundsString) {
    landingParams.bounds = boundsString;
  }
  if (origin?.lat != null && origin?.lng != null) {
    landingParams.origin = origin;
  }
  if (dateRangeYMD) {
    landingParams.startDate = dateRangeYMD.start;
    landingParams.endDate = dateRangeYMD.end;
    landingParams.date = dateRangeYMD.start;
  }

  return landingParams;
};

/**
 * Returns true when at least one meaningful search field is set.
 */
export const hasActiveSearchCriteria = params => {
  if (!params || typeof params !== 'object') {
    return false;
  }

  return Object.values(params).some(value => {
    if (value == null || value === '') {
      return false;
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value).length > 0;
    }
    return true;
  });
};
